import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ComponentRef,
    Injector,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Optional,
    Renderer2,
    ViewChild,
    ViewContainerRef,
    ViewRef
} from '@angular/core';
import { TimeoutService } from '@df/ng/timeout.service';
import { ICMS_CLIENT_CONFIG, NICmsClientConfig } from '@icms/client/config/icms-client.config';
import { ICmsContentService } from '@icms/core/content/icms-content.service';
import { NICmsContentComponent } from '@icms/core/content/interfaces/icms-content-component.interfaces';
import { ICmsDataChangeService } from '@icms/core/data/change/icms-data-change.service';
import each from 'lodash-es/each';
import { Subscription } from 'rxjs';
import { ICmsOutletComponent } from '../outlet/icms-outlet.component';
import { ICmsUtilViewContainerDirective } from '@icms/core/util/view-container/icms-util-view-container.directive';
@Component({
    standalone: false,
    selector: 'icms-component, [iCmsComponent]',
    template: `<div [ngClass]="wrapperClasses">
        <ng-template iCmsUtilViewContainer></ng-template>
    </div>`,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ICmsComponentComponent implements NICmsContentComponent.IEditableComponent, OnInit, OnChanges, OnDestroy {
    @Input()
    data!: NICmsContentComponent.IDataWithStyles | NICmsContentComponent.IData;

    @Input()
    iCmsComponent!: NICmsContentComponent.IDataWithStyles | NICmsContentComponent.IData;

    @Input()
    outletOptions?: NICmsContentComponent.IOutletOptions;

    /**
     * Embedded component reference
     */
    private componentRef?: ComponentRef<NICmsContentComponent.IComponent>;

    /**
     * If componetn has been initiated
     */
    private initiated = false;

    private meta?: NICmsContentComponent.IMeta;

    private subscriptions = new Subscription();

    @ViewChild(ICmsUtilViewContainerDirective, { static: true })
    private wrapeprRef!: ICmsUtilViewContainerDirective;

    protected wrapperClasses: string = '';
    /**
     * Dependencies
     */
    constructor(
        private iCmsContentService: ICmsContentService,
        readonly injector: Injector,
        private renderer2: Renderer2,
        private iCmsDataChangeService: ICmsDataChangeService,
        private changeDetectorRef: ChangeDetectorRef,
        @Optional() readonly outlet?: ICmsOutletComponent
    ) {}

    ngOnInit() {
        this.subscriptions.add(
            this.iCmsDataChangeService.afterChange$.subscribe(event => {
                if (this.initiated && event.changedObject === this.styles) {
                    this.applyStyles();
                    this.detectChanges();
                }
            })
        );
        this.update(true);
        this.initiated = true;
    }

    ngOnChanges(changes) {
        if (changes['iCmsComponent']) {
            this.data = this.iCmsComponent;
        }
        this.update();
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    private update(force = false) {
        if (this.initiated || force) {
            const componentId = this.data.id;
            if (!this.componentRef || this.meta?.id !== componentId) {
                this.meta = componentId ? this.iCmsContentService.getComponentMeta(componentId) : undefined;
                this.reRender();
            }
            this.applyData();
        }
    }

    private reRender() {
        if (this.wrapeprRef) {
            // this.wrapeprRef.viewContainerRef.clear();
            this.componentRef?.destroy();
            this.componentRef = undefined;
            if (this.meta) {
                this.componentRef = this.wrapeprRef.viewContainerRef.createComponent(this.meta.type);
            }
        }
    }

    private applyData() {
        // inject component data under block content or component
        if (this.componentRef) {
            this.componentRef.instance._setICmsContent?.(this.data);

            if (this.outletOptions && this.componentRef?.instance) {
                this.componentRef.instance.outletOptions = { ...this.outletOptions };
            }

            this.applyStyles();
            this.detectChanges();
            if (this.componentRef.instance.initiated) {
                this.componentRef.changeDetectorRef.detectChanges();
            }
        }
    }

    private get styles() {
        return (this.data as NICmsContentComponent.IDataWithStyles).styles;
    }

    private applyStyles() {
        const node = this.node;

        const styles = this.styles ? { ...this.styles } : {};
        const expectedClasses: string[] = [];

        // width integration
        const width = styles.width;
        if (width) {
            expectedClasses.push(`c-${width}`);
            if (!styles.mb) {
                styles.mb = '0';
            }
            if (!styles.mr) {
                styles.mr = '0';
            }

            this.wrapperClasses = 'wrap flex flex-justify-center';
        } else {
            this.wrapperClasses = '';
        }

        // cms component need to be block or inline-block display
        this.renderer2.setStyle(node, 'display', 'block');
        styles.sectionId ? this.renderer2.setAttribute(node, 'id', styles.sectionId) : this.renderer2.removeAttribute(node, 'id');
        each(
            {
                mt: 'margin-top',
                mb: 'margin-bottom',
                ml: 'margin-left',
                mr: 'margin-right',
                pt: 'padding-top',
                pb: 'padding-bottom',
                pl: 'padding-left',
                pr: 'padding-right'
            },
            (property, key) => {
                const value = styles[key];
                value ? this.renderer2.setStyle(node, property, value) : this.renderer2.removeStyle(node, property);
            }
        );

        const iCmsConfig = this.injector.get<NICmsClientConfig.IConfig>(ICMS_CLIENT_CONFIG);
        const styleValue = (styles.bg && iCmsConfig.colors.find(color => color.key === styles.bg)?.value) || styles.bg || undefined;
        styleValue ? this.renderer2.setStyle(node, 'background-color', styleValue) : this.renderer2.removeStyle(node, 'background-color');

        this.appliedClasses.filter(c => !expectedClasses.includes(c)).forEach(c => this.renderer2.removeClass(node, c));
        expectedClasses.filter(c => !this.appliedClasses.includes(c)).forEach(c => this.renderer2.addClass(node, c));
        this.appliedClasses = expectedClasses;
    }

    private appliedClasses: string[] = [];

    get node() {
        return this.componentRef!.location.nativeElement as HTMLElement;
    }

    private detectChanges() {
        if (this.initiated) {
            if (!(<ViewRef>this.changeDetectorRef).destroyed) {
                this.changeDetectorRef.detectChanges();
            }
        }
    }
}
